// SMS segment counting, ported from Twilio's Message Segment Calculator
// (https://twiliodeved.github.io/message-segment-calculator/, source
// github.com/TwilioDevEd/message-segment-calculator ``src/libs``). The backend
// twin is ``demo/lib/sms_segments.py``, which decides SMS vs MMS and applies
// Twilio's Smart Encoding (``smartEncode`` here) to every SMS before sending.
//
// * One character outside GSM-7 (an emoji, a pasted curly quote or em dash)
//   switches the whole message to UCS-2: 70 per segment instead of 160.
// * GSM-7 extension characters (^ { } \ [ ~ ] | € and form feed) are sent as
//   ESC + char, so they take two of the 160.
// * UCS-2 counts UTF-16 code units, so an emoji outside the BMP takes two of
//   the 70 even though it reads as one character.
// * Once a message needs more than one segment, every segment loses 6 octets
//   to the concatenation header: 153 GSM-7 / 67 UCS-2 per segment. A
//   character (grapheme) is never split across two segments.

import SMART_ENCODING_MAP from './smartEncodingMap';

const GSM7_BASIC = new Set(
    '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ\x1bÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?'
    + '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà',
);
const GSM7_EXTENSION = new Set('\f^{}\\[~]|€');

export const GSM7 = 'GSM-7';
export const UCS2 = 'UCS-2';

const SEGMENT_BITS = 1120; // 140 octets
const UDH_BITS = 48; // concatenation header, per segment of a multi-part message

const segmenter = typeof Intl !== 'undefined' && Intl.Segmenter
    ? new Intl.Segmenter(undefined, { granularity: 'grapheme' })
    : null;

// Graphemes, with CRLF split back into two characters the way the calculator
// does. Without Intl.Segmenter, code points (multi-code-point emoji then may
// split at a segment boundary — off by at most one segment).
function splitGraphemes(text) {
    const graphemes = segmenter
        ? Array.from(segmenter.segment(text), (s) => s.segment)
        : Array.from(text);
    return graphemes.flatMap((g) => (g === '\r\n' ? ['\r', '\n'] : [g]));
}

const isGsm7 = (g) => g.length === 1 && (GSM7_BASIC.has(g) || GSM7_EXTENSION.has(g));

/**
 * @returns {{encoding: string, characters: number, segments: number, nonGsmChars: string[]}}
 *   ``characters`` as the calculator displays them (GSM-7 extension characters
 *   count 2; UCS-2 counts graphemes). ``nonGsmChars`` are the distinct
 *   characters that forced UCS-2, in order of first appearance.
 */
export function smsSegmentInfo(text) {
    const graphemes = splitGraphemes(text || '');
    const nonGsmChars = [...new Set(graphemes.filter((g) => !isGsm7(g)))];
    const encoding = nonGsmChars.length ? UCS2 : GSM7;
    const bits = graphemes.map((g) => {
        if (encoding === GSM7) return GSM7_EXTENSION.has(g) ? 14 : 7;
        return 16 * g.length; // UTF-16 code units
    });
    const characters = encoding === GSM7
        ? bits.reduce((n, b) => n + b / 7, 0)
        : graphemes.length;

    let segments = 1;
    if (bits.reduce((n, b) => n + b, 0) > SEGMENT_BITS) {
        const capacity = SEGMENT_BITS - UDH_BITS;
        let used = 0;
        bits.forEach((b) => {
            if (used + b > capacity) {
                segments += 1;
                used = 0;
            }
            used += b;
        });
    }
    return { encoding, characters, segments, nonGsmChars };
}

/**
 * Twilio Smart Encoding, as the backend applies it before every SMS: curly
 * quotes, long dashes, ellipses, odd spaces and full-width forms become their
 * GSM-7 equivalents — but only when that makes the whole message GSM-7. If an
 * emoji, bullet or accented letter outside GSM-7 keeps it Unicode anyway, the
 * text is left exactly as written (all or nothing, like the backend).
 */
export function smartEncode(text) {
    const original = text || '';
    const encoded = Array.from(original, (c) => SMART_ENCODING_MAP[c] ?? c).join('');
    return encoded !== original && Array.from(encoded).every(isGsm7) ? encoded : original;
}

// Django template syntax in a campaign body: ``{{ variable }}`` renders to
// something per recipient (length unknown here), ``{% tag %}`` and
// ``{# comment #}`` render to nothing themselves.
const VARIABLE_RE = /\{\{[\s\S]*?\}\}/g;
const TAG_RE = /\{%[\s\S]*?%\}|\{#[\s\S]*?#\}/g;

/**
 * Count what an SMS recipient of ``body`` gets, as far as it can be known
 * while composing: the static text plus the opt-out line the send appends,
 * smart-encoded the way the send does. ``converted`` lists the characters
 * that encoding swaps for plain ones.
 * Variables are left out of the count and reported separately; text inside
 * ``{% if %}`` branches is all counted, as if every branch were sent.
 */
export function campaignSmsEstimate(body, optOutSuffix = '') {
    const text = body || '';
    const variables = (text.match(VARIABLE_RE) || []).length;
    const tags = (text.match(TAG_RE) || []).length;
    const staticText = `${text.replace(TAG_RE, '').replace(VARIABLE_RE, '')}${optOutSuffix || ''}`;
    const encoded = smartEncode(staticText);
    const converted = encoded === staticText
        ? []
        : [...new Set(Array.from(staticText).filter((c) => c in SMART_ENCODING_MAP))];
    const info = smsSegmentInfo(encoded);
    return { ...info, variables, hasTemplateLogic: tags > 0, converted };
}

// Twilio refuses an SMS or WhatsApp body over 1,600 characters (error 21617), and a campaign sends its text as one
// message (only the notification pipeline splits long ones), so a longer text reaches no guest at all. The backend
// refuses such a send (``_check_mobile_length`` in apiv2/lib/campaign_send.py); this is its twin for the composer.
export const MOBILE_MAX_CHARACTERS = 1600;
// A shortened attachment link plus its separator, as the backend counts it (``_ATTACHMENT_LINK_CHARS``).
export const ATTACHMENT_LINK_CHARACTERS = 40;

/**
 * The longer of the SMS and the WhatsApp text a guest of ``body`` gets, counted like ``campaignSmsEstimate``
 * (variables left out), each with its own channel's opt-out line and a short link per attachment that channel can
 * only send as a link (``smsLinks`` / ``whatsappLinks``).
 *
 * ``over``: certainly too long — variables only add to the count. A text with template tags is never ``over``,
 * because every ``{% if %}`` branch is counted as if all of them were sent; the backend doesn't refuse it either.
 */
export function campaignMobileLength(body, {
    smsOptOutSuffix = '', whatsappOptOutSuffix = '', smsLinks = 0, whatsappLinks = 0,
} = {}) {
    const legs = [
        [smsOptOutSuffix, smsLinks],
        // An older backend sends no WhatsApp line: the SMS one is 5 characters shorter.
        [whatsappOptOutSuffix || smsOptOutSuffix, whatsappLinks],
    ].map(([suffix, links]) => {
        const est = campaignSmsEstimate(body, suffix);
        return { est, links, characters: est.characters + ATTACHMENT_LINK_CHARACTERS * links };
    });
    // Ties go to SMS, like the backend.
    const longest = legs[1].characters > legs[0].characters ? legs[1] : legs[0];
    return {
        characters: longest.characters,
        withLinks: longest.links > 0,
        variables: longest.est.variables,
        hasTemplateLogic: longest.est.hasTemplateLogic,
        over: longest.characters > MOBILE_MAX_CHARACTERS && !longest.est.hasTemplateLogic,
    };
}
